import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import recetaService, { RecetaDto } from '../../services/recetaService';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import { catalogosService } from '../../services/catalogosService';
import { formatCurrency } from '../../utils/formatters';
import RecetaProductoLookup from './RecetaProductoLookup';
import RecetaComponentesGrid, {
  CantidadUmField,
  RecetaComponenteLinea,
  decimalesUnidad,
} from './RecetaComponentesGrid';
import { mapProductoToRecetaArticulo } from './RecetaArticuloSearchDialog';

const newLinea = (): RecetaComponenteLinea => ({
  id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  codigoArticulo: '',
  descripcion: '',
  unidadMedida: '',
  cantidad: 1,
  costo: 0,
  notas: '',
});

const cantidadOCero = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

const RecetaForm: React.FC = () => {
  const { codigoReceta } = useParams();
  const [searchParams] = useSearchParams();
  const codigoParaCopiar = searchParams.get('copiarDe')?.trim() || '';
  const isNew = !codigoReceta || codigoReceta === 'nuevo';
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [codigo, setCodigo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [notas, setNotas] = useState('');
  const [ptCodigo, setPtCodigo] = useState('');
  const [ptDescripcion, setPtDescripcion] = useState('');
  const [ptUnidadMedida, setPtUnidadMedida] = useState('');
  const [ptCantidad, setPtCantidad] = useState(1);
  const [ptCostoActual, setPtCostoActual] = useState(0);
  const [lineas, setLineas] = useState<RecetaComponenteLinea[]>([newLinea()]);
  const [saving, setSaving] = useState(false);
  const [updatingCosto, setUpdatingCosto] = useState(false);
  const [confirmCostoOpen, setConfirmCostoOpen] = useState(false);
  const [pendingCostoUnitario, setPendingCostoUnitario] = useState(0);
  const [decimalesPorUnidad, setDecimalesPorUnidad] = useState<Map<string, number>>(() => new Map());
  const [catalogoUnidadesListo, setCatalogoUnidadesListo] = useState(false);

  useEffect(() => {
    let cancelled = false;
    catalogosService
      .getUnidadesMedida(1, 1000)
      .then((result) => {
        if (cancelled) return;
        const mapa = new Map<string, number>();
        (result.items ?? []).forEach((unidad) => {
          const codigoUm = String(unidad.idUnidadMedida ?? '').trim().toUpperCase();
          if (!codigoUm) return;
          const decimales = Number(unidad.cantDecimales);
          mapa.set(codigoUm, Number.isFinite(decimales) && decimales > 0 ? Math.trunc(decimales) : 0);
        });
        setDecimalesPorUnidad(mapa);
        setCatalogoUnidadesListo(true);
      })
      .catch(() => {
        if (!cancelled) setCatalogoUnidadesListo(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const origen = isNew ? codigoParaCopiar : codigoReceta;
    if (!origen) return;

    let cancelled = false;
    (async () => {
      try {
        const data = await recetaService.getReceta(decodeURIComponent(origen));
        if (cancelled) return;

        const aplicar = async (receta: RecetaDto, copiar: boolean) => {
          setCodigo(copiar ? '' : receta.codigoReceta);
          setDescripcion(receta.descripcion ?? '');
          setNotas(receta.notas ?? '');
          const ptCode = receta.productoTerminado?.codigoArticulo ?? '';
          setPtCodigo(ptCode);
          setPtDescripcion(receta.productoTerminado?.descripcion ?? '');
          setPtCantidad(cantidadOCero(receta.productoTerminado?.cantidad ?? 0));

          if (ptCode) {
            try {
              const p = await productoService.getProducto(ptCode, OPTIONS_PRODUCTO_OPERATIVO);
              if (!cancelled) {
                const mapped = mapProductoToRecetaArticulo(p);
                setPtCostoActual(Number(p.costo ?? 0) || 0);
                setPtUnidadMedida(mapped.unidadMedida);
              }
            } catch {
              if (!cancelled) {
                setPtCostoActual(0);
                setPtUnidadMedida('');
              }
            }
          } else if (!cancelled) {
            setPtCostoActual(0);
            setPtUnidadMedida('');
          }

          const componentes = receta.componentes?.length ? receta.componentes : [];
          if (componentes.length === 0) {
            if (!cancelled) setLineas([newLinea()]);
            return;
          }

          const enriched = await Promise.all(
            componentes.map(async (c) => {
              const base: RecetaComponenteLinea = {
                id: `c-${c.codigoArticulo}-${Math.random().toString(36).slice(2, 8)}`,
                codigoArticulo: c.codigoArticulo,
                descripcion: c.descripcion ?? '',
                unidadMedida: '',
                cantidad: cantidadOCero(c.cantidad),
                costo: 0,
                notas: c.notas ?? '',
              };
              try {
                const p = await productoService.getProducto(c.codigoArticulo, OPTIONS_PRODUCTO_OPERATIVO);
                const mapped = mapProductoToRecetaArticulo(p);
                return {
                  ...base,
                  descripcion: mapped.descripcion || base.descripcion,
                  unidadMedida: mapped.unidadMedida,
                  costo: mapped.costo,
                };
              } catch {
                return base;
              }
            }),
          );
          if (!cancelled) setLineas(enriched);
        };

        await aplicar(data, Boolean(isNew && codigoParaCopiar));
      } catch {
        enqueueSnackbar(
          isNew && codigoParaCopiar
            ? 'No se pudo cargar la receta a copiar'
            : 'No se pudo cargar la receta',
          { variant: 'error' },
        );
        navigate('/produccion/recetas');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [codigoReceta, codigoParaCopiar, isNew, enqueueSnackbar, navigate]);

  const costoReceta = useMemo(
    () =>
      lineas
        .filter((l) => l.codigoArticulo.trim())
        .reduce((acc, l) => acc + (Number(l.cantidad) || 0) * (Number(l.costo) || 0), 0),
    [lineas],
  );

  const costoUnitarioPt = useMemo(() => {
    const qty = Number(ptCantidad) || 0;
    if (qty <= 0) return 0;
    return costoReceta / qty;
  }, [costoReceta, ptCantidad]);

  const ptDecimales = decimalesUnidad(ptUnidadMedida, decimalesPorUnidad, catalogoUnidadesListo);
  const ptUmLabel = (() => {
    const um = String(ptUnidadMedida ?? '').trim();
    return um && um !== '—' ? um : '';
  })();

  const finishAndNavigate = () => {
    setConfirmCostoOpen(false);
    navigate('/produccion/recetas');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const componentes = lineas
        .filter((c) => c.codigoArticulo.trim())
        .map((c) => ({
          codigoArticulo: c.codigoArticulo.trim(),
          descripcion: (c.descripcion ?? '').trim(),
          cantidad: cantidadOCero(c.cantidad),
          notas: (c.notas ?? '').trim() || null,
        }));

      const payload = {
        descripcion: descripcion.trim(),
        notas: notas.trim() || null,
        productoTerminado: {
          codigoArticulo: ptCodigo.trim(),
          descripcion: ptDescripcion.trim(),
          cantidad: cantidadOCero(ptCantidad),
        },
        componentes,
      };

      if (isNew) {
        await recetaService.create({ codigoReceta: codigo.trim(), ...payload });
      } else {
        await recetaService.update(codigo.trim(), payload);
      }
      enqueueSnackbar('Receta guardada', { variant: 'success' });

      const codigoPt = ptCodigo.trim();
      if (codigoPt) {
        try {
          const p = await productoService.getProducto(codigoPt, OPTIONS_PRODUCTO_OPERATIVO);
          const costoFicha =
            Number(
              (p as { costoCatalogo?: number }).costoCatalogo
                ?? (p as { descuentos?: { costoCatalogo?: number }[] }).descuentos?.[0]?.costoCatalogo
                ?? p.costo
                ?? 0,
            ) || 0;
          setPtCostoActual(costoFicha);
        } catch {
          // Se mantiene el costo en memoria si no se puede refrescar
        }
        setPendingCostoUnitario(costoUnitarioPt);
        setConfirmCostoOpen(true);
      } else {
        navigate('/produccion/recetas');
      }
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al guardar la receta';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmarActualizarCosto = async () => {
    try {
      setUpdatingCosto(true);
      await recetaService.actualizarCostoProductoTerminado(ptCodigo.trim(), pendingCostoUnitario);
      enqueueSnackbar('Costo del producto terminado actualizado', { variant: 'success' });
      finishAndNavigate();
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al actualizar el costo del producto';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setUpdatingCosto(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/produccion/recetas')} sx={{ textTransform: 'none' }}>
              Recetas
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isNew ? (codigoParaCopiar ? 'Copiar receta' : 'Nueva receta') : 'Editar receta'}
            </Typography>
          </Stack>

          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Código de receta"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                disabled={!isNew}
                required
                fullWidth
                helperText={
                  isNew && codigoParaCopiar
                    ? `Copia de ${codigoParaCopiar}. Asigná un código nuevo.`
                    : undefined
                }
              />
              <TextField
                label="Descripción"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                required
                fullWidth
              />
            </Stack>

            <Typography variant="h6">Producto a fabricar</Typography>
            <RecetaProductoLookup
              label="Producto a fabricar"
              codigo={ptCodigo}
              descripcion={ptDescripcion}
              required
              tiposArticulo="PT,SE"
              dialogTitle="Buscar producto a fabricar"
              mensajeTipoInvalido="El artículo debe ser Producto Terminado o Semi Elaborado"
              onCodigoChange={(value) => {
                setPtCodigo(value);
                setPtCostoActual(0);
                setPtUnidadMedida('');
              }}
              onDescripcionChange={setPtDescripcion}
              onResolved={(item) => {
                setPtCodigo(item.codigoArticulo);
                setPtDescripcion(item.descripcion);
                setPtCostoActual(item.costo);
                setPtUnidadMedida(item.unidadMedida);
              }}
            />
            <CantidadUmField
              label="Cantidad de producto"
              value={ptCantidad}
              decimales={ptDecimales}
              unidadMedida={ptUmLabel}
              onChange={setPtCantidad}
              sx={{ maxWidth: 280 }}
            />

            <RecetaComponentesGrid
              lineas={lineas}
              onLineaChange={(id, field, value) => {
                setLineas((prev) =>
                  prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
                );
              }}
              onLineaFilled={(id, data) => {
                setLineas((prev) =>
                  prev.map((row) => (row.id === id ? { ...row, ...data } : row)),
                );
              }}
              onEliminarLinea={(id) => {
                setLineas((prev) => (prev.length <= 1 ? prev : prev.filter((row) => row.id !== id)));
              }}
              onAgregarLineaVacia={() => setLineas((prev) => [...prev, newLinea()])}
              onMoverLinea={(id, direccion) => {
                setLineas((prev) => {
                  const idx = prev.findIndex((row) => row.id === id);
                  if (idx < 0) return prev;
                  const destino = idx + direccion;
                  if (destino < 0 || destino >= prev.length) return prev;
                  const next = [...prev];
                  const [item] = next.splice(idx, 1);
                  next.splice(destino, 0, item);
                  return next;
                });
              }}
            />

            <TextField
              label="Notas / procedimientos"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              placeholder="Pasos de elaboración, temperaturas, tiempos, controles, etc."
              helperText="Texto libre para documentar el procedimiento de la receta."
            />

            <Divider />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Costo actual de la receta: {formatCurrency(costoReceta)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Costo unitario (según cantidad {ptCantidad || 0}
                  {ptUmLabel ? ` ${ptUmLabel}` : ''}): {formatCurrency(costoUnitarioPt)}
                  {ptUmLabel ? ` / ${ptUmLabel}` : ''}
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                Guardar
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirmCostoOpen} onClose={finishAndNavigate} maxWidth="xs" fullWidth>
        <DialogTitle>Actualizar costo del producto</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body1" sx={{ mb: 1.5 }}>
              ¿Desea actualizar el costo del producto en la ficha del producto?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Producto: {ptCodigo.trim()} — {ptDescripcion.trim() || '—'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Costo actual (ficha): {formatCurrency(ptCostoActual)}
              {ptUmLabel ? ` / ${ptUmLabel}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Nuevo costo (según receta): {formatCurrency(pendingCostoUnitario)}
              {ptUmLabel ? ` / ${ptUmLabel}` : ''}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={finishAndNavigate} disabled={updatingCosto}>
            No
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmarActualizarCosto}
            disabled={updatingCosto}
          >
            Sí, actualizar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecetaForm;
